import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL;

function AdminHigherEducationDashboard({ getToken }) {
  const tokenGetter = getToken || (() => localStorage.getItem('gogig_admin_access'));
  const [applications, setApplications] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, [selectedMonth, page, limit]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      if (selectedMonth) {
        params.append('month', selectedMonth);
      }
      
      const token = typeof tokenGetter === 'function' ? tokenGetter() : tokenGetter;
      const url = `${API_URL}/api/admin/higher-education/applications?${params}`;
      const res = await fetch(url, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const data = await res.json();
      if (data.success) {
        setApplications(data.applications);
        setTotal(data.total);
        setTotalPages(data.totalPages);
        setHasNextPage(data.hasNextPage);
        setHasPrevPage(data.hasPrevPage);
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const token = typeof tokenGetter === 'function' ? tokenGetter() : tokenGetter;
      const downloadUrl = selectedMonth
        ? `${API_URL}/api/admin/higher-education/applications/download?month=${selectedMonth}`
        : `${API_URL}/api/admin/higher-education/applications/download`;
      
      const res = await fetch(downloadUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `higher-education-applications-${selectedMonth || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading report:', err);
      alert('Failed to download report');
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
    setPage(1); // Reset to first page when changing limit
  };

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Higher Education Applications
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          type="month"
          label="Filter by Month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Items per page</InputLabel>
          <Select
            value={limit}
            label="Items per page"
            onChange={handleLimitChange}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={loading}
        >
          Download Report
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Candidate Name</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Course Type</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Company Name</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applications.map((app) => (
              <TableRow key={app.id}>
                <TableCell>
                  {new Date(app.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell>{app.candidate_name}</TableCell>
                <TableCell>{app.mobile}</TableCell>
                <TableCell>{app.email}</TableCell>
                <TableCell>{app.course_type}</TableCell>
                <TableCell>{app.type || '-'}</TableCell>
                <TableCell>{app.company_name || '-'}</TableCell>
                <TableCell>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      backgroundColor: app.status === 'Employed' ? '#e8f5e8' : '#fff3e0',
                      color: app.status === 'Employed' ? '#2e7d32' : '#f57c00',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      textAlign: 'center',
                      minWidth: 80
                    }}
                  >
                    {app.status || 'Unemployed'}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {applications.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {loading ? 'Loading...' : 'No applications found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {total > 0 && (
        <Box sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
          Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} applications
        </Box>
      )}
    </Card>
  );
}

export default AdminHigherEducationDashboard; 